import React, { useState, useEffect, useMemo } from 'react';
import { OrdenesTrabajoProvider, useOrdenesTrabajoContext } from '@/contexts/OrdenesTrabajoContext';

import { VistaSemanalConBandeja } from '@/components/ordenes/VistaSemanalConBandeja';
import { OrdenesListaView } from '@/components/ordenes/OrdenesListaView';
import { OrdenTrabajoForm } from '@/components/ordenes/OrdenTrabajoForm';
import { useAuth } from '@/contexts/AuthContext';
import { useSecurityContext } from '@/contexts/MaintenanceContext';
import { supabase } from '@/integrations/supabase/client';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  CalendarDays,
  ClipboardList,
  Inbox,
  Archive,
  Search,
  Users,
  TrendingUp,
  List,
  LayoutGrid,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Tecnico {
  user_id: string;
  nombre: string;
  avatar_url?: string | null;
}

type Etapa = 'planificar' | 'planificadas' | 'historico';

const AsignacionTareasContent: React.FC = () => {
  const { ordenes, loading: ordenesLoading, refreshOrdenes } = useOrdenesTrabajoContext();
  const { perfil } = useAuth();
  const { clientes } = useSecurityContext();
  const [tecnicos, setTecnicos] = useState<Tecnico[]>([]);
  const [loadingTecnicos, setLoadingTecnicos] = useState(true);
  const [etapa, setEtapa] = useState<Etapa>('planificar');
  const [vistaPlanificadas, setVistaPlanificadas] = useState<'semana' | 'lista'>('semana');

  const [filtros, setFiltros] = useState({
    busqueda: '',
    estado: 'all',
    prioridad: 'all',
    tecnico: 'all',
  });

  // Cargar técnicos + admins (pueden recibir asignaciones también)
  useEffect(() => {
    const load = async () => {
      if (!perfil?.empresa_id) return;
      try {
        const { data, error } = await supabase
          .from('perfiles')
          .select('user_id, nombre, avatar_url')
          .eq('empresa_id', perfil.empresa_id)
          .in('rol', ['tecnico', 'admin'])
          .eq('estado', 'activo');
        if (error) throw error;
        setTecnicos(data || []);
      } catch (e) {
        console.error('Error cargando técnicos:', e);
      } finally {
        setLoadingTecnicos(false);
      }
    };
    load();
  }, [perfil?.empresa_id]);

  const loading = ordenesLoading || loadingTecnicos;

  const ordenesConCliente = useMemo(
    () =>
      ordenes.map((o) => ({
        ...o,
        cliente_nombre: clientes?.find((c) => c.id === o.cliente_id)?.nombre,
      })),
    [ordenes, clientes]
  );

  // Particionar por ciclo de vida
  const { porPlanificar, planificadas, historico } = useMemo(() => {
    const porPlanificar: typeof ordenesConCliente = [];
    const planificadas: typeof ordenesConCliente = [];
    const historico: typeof ordenesConCliente = [];

    for (const o of ordenesConCliente) {
      if (o.estado === 'finalizada' || o.estado === 'cancelada') {
        historico.push(o);
        continue;
      }
      const tieneTecnico =
        !!o.tecnico_asignado_id || (o.tecnicos_asignados && o.tecnicos_asignados.length > 0);
      const tieneFecha = !!o.fecha_limite;
      if (!tieneTecnico || !tieneFecha) {
        porPlanificar.push(o);
      } else {
        planificadas.push(o);
      }
    }
    return { porPlanificar, planificadas, historico };
  }, [ordenesConCliente]);

  const stats = useMemo(
    () => ({
      porPlanificar: porPlanificar.length,
      planificadas: planificadas.length,
      historico: historico.length,
      tecnicos: tecnicos.length,
    }),
    [porPlanificar, planificadas, historico, tecnicos]
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-96" />
        <div className="flex gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24 flex-1" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Gestión de OTs</h1>
          <p className="text-sm text-muted-foreground">
            Crea, planifica y haz seguimiento de las órdenes de trabajo en tres pasos.
          </p>
        </div>
        <OrdenTrabajoForm onSuccess={refreshOrdenes} />
      </div>

      {/* Stats compactas */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="p-3 bg-gradient-to-br from-amber-500/10 to-amber-500/5 border-amber-500/30">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-md bg-amber-500/20">
              <Inbox className="h-4 w-4 text-amber-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Por planificar</p>
              <p className="text-lg font-bold text-amber-500">{stats.porPlanificar}</p>
            </div>
          </div>
        </Card>
        <Card className="p-3 bg-card/50 border-border/50">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-md bg-blue-500/10">
              <CalendarDays className="h-4 w-4 text-blue-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Planificadas</p>
              <p className="text-lg font-bold text-blue-500">{stats.planificadas}</p>
            </div>
          </div>
        </Card>
        <Card className="p-3 bg-card/50 border-border/50">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-md bg-emerald-500/10">
              <TrendingUp className="h-4 w-4 text-emerald-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Histórico</p>
              <p className="text-lg font-bold">{stats.historico}</p>
            </div>
          </div>
        </Card>
        <Card className="p-3 bg-card/50 border-border/50">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-md bg-purple-500/10">
              <Users className="h-4 w-4 text-purple-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Técnicos</p>
              <p className="text-lg font-bold">{stats.tecnicos}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Pestañas de ciclo de vida */}
      <Tabs value={etapa} onValueChange={(v) => setEtapa(v as Etapa)} className="space-y-4">
        <TabsList className="grid grid-cols-3 w-full max-w-2xl">
          <TabsTrigger value="planificar" className="gap-2">
            <Inbox className="h-4 w-4" />
            <span className="hidden sm:inline">Por planificar</span>
            <span className="sm:hidden">Planificar</span>
            <span className="ml-1 text-xs opacity-70">({stats.porPlanificar})</span>
          </TabsTrigger>
          <TabsTrigger value="planificadas" className="gap-2">
            <CalendarDays className="h-4 w-4" />
            <span className="hidden sm:inline">Planificadas</span>
            <span className="sm:hidden">Semana</span>
            <span className="ml-1 text-xs opacity-70">({stats.planificadas})</span>
          </TabsTrigger>
          <TabsTrigger value="historico" className="gap-2">
            <Archive className="h-4 w-4" />
            <span className="hidden sm:inline">Histórico</span>
            <span className="sm:hidden">Hist.</span>
            <span className="ml-1 text-xs opacity-70">({stats.historico})</span>
          </TabsTrigger>
        </TabsList>

        {/* POR PLANIFICAR */}
        <TabsContent value="planificar" className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="relative sm:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar OT, cliente o número..."
                value={filtros.busqueda}
                onChange={(e) => setFiltros((p) => ({ ...p, busqueda: e.target.value }))}
                className="pl-9 bg-muted/50"
              />
            </div>
            <Select
              value={filtros.prioridad}
              onValueChange={(v) => setFiltros((p) => ({ ...p, prioridad: v }))}
            >
              <SelectTrigger className="bg-muted/50">
                <SelectValue placeholder="Prioridad" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las prioridades</SelectItem>
                <SelectItem value="urgente">Urgente</SelectItem>
                <SelectItem value="alta">Alta</SelectItem>
                <SelectItem value="media">Media</SelectItem>
                <SelectItem value="baja">Baja</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <OrdenesListaView
            ordenes={porPlanificar}
            filtros={{ ...filtros, estado: 'all', tecnico: 'all' }}
            showFaltantes
            tecnicos={tecnicos}
            emptyMessage={{
              title: '¡Todo planificado!',
              subtitle: 'No hay OTs pendientes de asignar técnico o fecha.',
            }}
          />
        </TabsContent>

        {/* PLANIFICADAS — vista semanal con bandeja o lista */}
        <TabsContent value="planificadas" className="space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="inline-flex rounded-md border bg-muted/40 p-0.5">
              <Button
                size="sm"
                variant={vistaPlanificadas === 'semana' ? 'default' : 'ghost'}
                className="h-8 gap-1.5 px-3"
                onClick={() => setVistaPlanificadas('semana')}
              >
                <LayoutGrid className="h-3.5 w-3.5" />
                Semana
              </Button>
              <Button
                size="sm"
                variant={vistaPlanificadas === 'lista' ? 'default' : 'ghost'}
                className="h-8 gap-1.5 px-3"
                onClick={() => setVistaPlanificadas('lista')}
              >
                <List className="h-3.5 w-3.5" />
                Lista
              </Button>
            </div>
          </div>

          {vistaPlanificadas === 'semana' ? (
            <VistaSemanalConBandeja
              ordenes={ordenesConCliente}
              tecnicos={tecnicos}
              clientes={clientes || []}
              selectedTecnicos={[]}
            />
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="relative sm:col-span-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar OT, cliente..."
                    value={filtros.busqueda}
                    onChange={(e) => setFiltros((p) => ({ ...p, busqueda: e.target.value }))}
                    className="pl-9 bg-muted/50"
                  />
                </div>
                <Select
                  value={filtros.prioridad}
                  onValueChange={(v) => setFiltros((p) => ({ ...p, prioridad: v }))}
                >
                  <SelectTrigger className="bg-muted/50">
                    <SelectValue placeholder="Prioridad" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las prioridades</SelectItem>
                    <SelectItem value="urgente">Urgente</SelectItem>
                    <SelectItem value="alta">Alta</SelectItem>
                    <SelectItem value="media">Media</SelectItem>
                    <SelectItem value="baja">Baja</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={filtros.tecnico}
                  onValueChange={(v) => setFiltros((p) => ({ ...p, tecnico: v }))}
                >
                  <SelectTrigger className="bg-muted/50">
                    <SelectValue placeholder="Técnico" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los técnicos</SelectItem>
                    {tecnicos.map((t) => (
                      <SelectItem key={t.user_id} value={t.user_id}>
                        {t.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <OrdenesListaView
                ordenes={planificadas}
                filtros={{ ...filtros, estado: 'all' }}
                tecnicos={tecnicos}
                emptyMessage={{
                  title: 'Sin OTs planificadas',
                  subtitle: 'Las OTs con técnico y fecha aparecerán aquí.',
                }}
              />
            </div>
          )}
        </TabsContent>

        {/* HISTÓRICO */}
        <TabsContent value="historico" className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar OT, cliente..."
                value={filtros.busqueda}
                onChange={(e) => setFiltros((p) => ({ ...p, busqueda: e.target.value }))}
                className="pl-9 bg-muted/50"
              />
            </div>
            <Select
              value={filtros.estado}
              onValueChange={(v) => setFiltros((p) => ({ ...p, estado: v }))}
            >
              <SelectTrigger className="bg-muted/50">
                <SelectValue placeholder="Resultado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Finalizadas y canceladas</SelectItem>
                <SelectItem value="finalizada">Finalizadas</SelectItem>
                <SelectItem value="cancelada">Canceladas</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filtros.tecnico}
              onValueChange={(v) => setFiltros((p) => ({ ...p, tecnico: v }))}
            >
              <SelectTrigger className="bg-muted/50">
                <SelectValue placeholder="Técnico" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los técnicos</SelectItem>
                {tecnicos.map((t) => (
                  <SelectItem key={t.user_id} value={t.user_id}>
                    {t.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <OrdenesListaView
            ordenes={historico}
            filtros={{ ...filtros, prioridad: 'all' }}
            tecnicos={tecnicos}
            emptyMessage={{
              title: 'Sin histórico',
              subtitle: 'Las OTs finalizadas o canceladas aparecerán aquí.',
            }}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

const AsignacionTareasPage: React.FC = () => {
  return (
    <OrdenesTrabajoProvider>
      <AsignacionTareasContent />
    </OrdenesTrabajoProvider>
  );
};

export default AsignacionTareasPage;
